import { CoreModalDialogFooter, FileUploadedPane, FileUploadPane, MMButton } from '@components'
import DeleteIcon from '@images/icons/batch-delete.svg?react'
import UploadedIcon from '@images/icons/batch-uploaded.svg?react'
import settings from '@settings'
import { useAttachDocumentsMutation, useTransactionFilesManagementQry } from 'mmfintech-backend-api'
import { checkFileUpload, GlobalContext, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import styles from './DocumentRequestUploadModal.module.scss'

interface DocumentRequestUploadModalProps {
  docRequestId: number
  // Optional override for the close action (Cancel or a successful submit); falls back to a
  // plain modalHide() when not given. The popup used to pass its own reopen-with-remaining-
  // requests handler here, but that was reverted (2026-09-04) after repeatedly reproducing a
  // stale-reopen bug live — every entry point now just closes the modal outright.
  onClose?: () => void
}

export const DocumentRequestUploadModal = ({ docRequestId, onClose }: DocumentRequestUploadModalProps) => {
  const { modalHide } = useContext(GlobalContext)
  const handleClose = onClose ?? modalHide

  const { uploadFile, uploadFileError, uploadFileFetching, removeFile, resetFiles, getDocumentIds, uploadedFiles } =
    useTransactionFilesManagementQry()

  const [attachDocuments, { isLoading: attachFetching, error: attachError }] = useAttachDocumentsMutation()

  const [file, setFile] = useState<File>(null)
  const [fileError, setFileError] = useState(null)
  const [comment, setComment] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only reset, resetFiles identity is not relevant here
  useEffect(() => {
    resetFiles()
  }, [])

  const handleFileSelected = (files: File[] | FileList) => {
    setFileError(null)
    const list = Array.from(files)
    if (list.length) {
      const selected = list[0]
      setFile(selected)
      void uploadFile({ file: selected, type: 'OTHER' }).then(() => setFile(null))
    }
  }

  const handleFileError = (errors: any) => setFileError(checkFileUpload(errors))

  const handleSubmit = async () => {
    try {
      await attachDocuments({ docRequestId, documentIds: getDocumentIds(), uploadComment: comment }).unwrap()
      resetFiles()
      handleClose()
    } catch {}
  }

  return (
    <div className={styles['document-request-upload-wrapper']} data-test='document-request-upload-modal'>
      {file ? (
        <FileUploadedPane file={file} onRemove={() => setFile(null)} />
      ) : (
        <FileUploadPane
          dragAndDropMessage={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.DRAG_DROP', 'Drag & Drop File')}
          acceptTypes={settings.questionnaireFileTypes}
          maxFileSize={settings.questionnaireMaxFileSize}
          onDrop={handleFileSelected}
          onError={handleFileError}
          onRemove={() => setFile(null)}
        />
      )}

      {isValidArray(uploadedFiles) && (
        <div className={styles['uploaded-files-list']}>
          {uploadedFiles.map((f: any) => (
            <div className={styles['uploaded-file-row']} key={f.id}>
              <UploadedIcon />
              <span className={styles['uploaded-file-name']}>{f.fileName}</span>
              <div
                className={styles['uploaded-file-delete-icon']}
                data-test='remove-uploaded-file'
                onClick={() => removeFile(f.id)}
              >
                <DeleteIcon />
              </div>
            </div>
          ))}
        </div>
      )}

      <Input
        type='textarea'
        label={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.COMMENT_LABEL', 'Comment (optional)')}
        placeholder={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.COMMENT_PLACEHOLDER', 'Add a comment')}
        name='comment'
        value={comment}
        onChange={(_name, value) => setComment(value as string)}
        rows={4}
        maxLength={500}
      />

      <ErrorDisplay error={[fileError, uploadFileError, attachError]} />

      <CoreModalDialogFooter>
        <div className={styles.buttons}>
          <MMButton
            type='button'
            size='large'
            variant='primary'
            fullWidth
            caption={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.SUBMIT', 'Submit')}
            onClick={handleSubmit}
            disabled={!isValidArray(uploadedFiles)}
            loading={uploadFileFetching || attachFetching}
            data-test='button-submit'
          />
          <MMButton
            type='button'
            size='large'
            variant='tertiary'
            className={styles['cancel-button']}
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={handleClose}
            data-test='button-cancel'
          />
        </div>
      </CoreModalDialogFooter>
    </div>
  )
}
